import type { RouteConfig } from './types/routes'

/** Admin/staff paths match Angular URL shapes (no `/dashboard` parent prefix). */
export const ROUTE_PATHS = {
  ANGULAR_HOME: '/',
  LOGIN: '/login',
  SALES_TEAM: '/sales-team',
  DASHBOARD: '/dashboard',
  DASHBOARD_DOCUMENT_TERMS: '/terms-and-conditions',
  DASHBOARD_DOCUMENT_SERVICE: '/gdpr-consent/gdpr-document',
  DASHBOARD_COMPANIES: '/company',
  DASHBOARD_PROPERTIES: '/property',
  DASHBOARD_INVITEE: '/invite',
  DASHBOARD_YARDI_INVITE: '/yardiinvite',
  DASHBOARD_YARDI_MATCHING: '/yardi-matching',
  DASHBOARD_USER_MANAGEMENT: '/user-management',
  DASHBOARD_PRODUCTS: '/product',
  DASHBOARD_MODELS: '/model',
  DASHBOARD_APPLICATIONS: '/application',
  DASHBOARD_RESIDENTS: '/resident',
  /** Angular DefaultModule — `/defaults` (UI label remains "Reimbursements"). */
  DEFAULTS: '/defaults',
  /** @deprecated Use DEFAULTS — kept for call-site compatibility. */
  DASHBOARD_REIMBURSEMENTS: '/defaults',
  DASHBOARD_TRANSFER: '/transfer',
  DASHBOARD_REPORTS: '/reports',
  DASHBOARD_REPORTS_PAYMENT_LOGS: '/reports/payment-logs',
  DASHBOARD_REPORTS_DEFAULT_PAYMENT_LOGS: '/reports/default-payment-logs',
  DASHBOARD_REPORTS_CHARGEBACK: '/reports/chargeback',
  DASHBOARD_MAILTRAP: '/mailtrap',
  DASHBOARD_EMAIL_TEMPLATES: '/email-templates',
  DASHBOARD_JOBS_QUEUED: '/jobs/queued',
  DASHBOARD_JOBS_FAILED: '/jobs/failed',
  DASHBOARD_JOBS_CRON: '/jobs/cron',
  DASHBOARD_JOBS_CRON_MONITOR: '/jobs/cron-monitor',
  DASHBOARD_JOBS_CRON_MONITOR_LIST: '/jobs/cron-monitor/list',
  DASHBOARD_JOBS_CRON_MONITOR_FAILED: '/jobs/cron-monitor/list?kind=failed',
  DASHBOARD_JOBS_CRON_MONITOR_MISSED: '/jobs/cron-monitor/list?kind=missed',
  DASHBOARD_JOBS_CRON_MONITOR_STUCK: '/jobs/cron-monitor/list?kind=stuck',
  DASHBOARD_JOBS_CRON_MONITOR_WAITING: '/jobs/cron-monitor/list?kind=waiting',
  DASHBOARD_JOBS_CRON_MONITOR_RUNNING: '/jobs/cron-monitor/list?kind=running',
  DASHBOARD_JOBS_CRON_MONITOR_SUCCESSFUL: '/jobs/cron-monitor/list?kind=successful',
  DASHBOARD_HELP_ABOUT: '/help-about',
  DASHBOARD_PROFILE: '/user/profile',
  DASHBOARD_REPORTS_SCHEDULER: '/reports-scheduler',
  DASHBOARD_UNDERWRITING: '/underwriting-config',
  DASHBOARD_IMPACT_NOTES: '/impact-notes',
  DASHBOARD_REPORT_DESCRIPTION: '/report-description',
  DASHBOARD_WHATS_NEW: '/version',
  DASHBOARD_NOTIFICATION_SETTINGS: '/notification-settings',
  DASHBOARD_POLICY: '/policy',
  DASHBOARD_STOCK: '/stock',
  DASHBOARD_ORGANIZATIONS: '/organizations',
  DASHBOARD_SYSTEM_ANNOUNCEMENT: '/system-announcement',
  DASHBOARD_DASHBOARD_TILES: '/dashboard-tiles',
  DASHBOARD_ROLES: '/user-roles',
  DASHBOARD_PROPERTY_MAINTENANCE: '/property-maintenance',
  DASHBOARD_TRANSFER_LIST: '/transfer-list',
  DASHBOARD_IMPORT_DATA: '/import-data',
  DASHBOARD_CONFIG: '/config',
  DASHBOARD_SCREENING_INFO_REQUESTS: '/screening-info-requests',
  DASHBOARD_FREQUENTLY_USED_NOTES: '/frequently-used-notes',
  DASHBOARD_IMAP_LOGS: '/imap-logs',
  APPLICANT: '/applicant',
  APPLICANT_DASHBOARD: '/applicant/dashboard',
  APPLICANT_APPLICATION_ABOUT: '/applicant/application/about',
  PRE_SCREENING_INVITE: '/pre-screening/invite',
} as const

export const ROUTE_PATTERNS = {
  DASHBOARD_DOCUMENT_TERMS_EDIT: `${ROUTE_PATHS.DASHBOARD_DOCUMENT_TERMS}/edit/:policyId`,
  DASHBOARD_DOCUMENT_SERVICE_EDIT: '/gdpr-consent/edit/:documentId',
  DASHBOARD_DOCUMENT_SERVICE_VIEW: '/gdpr-consent/view/:documentId',
  DASHBOARD_COMPANY_EDIT: `${ROUTE_PATHS.DASHBOARD_COMPANIES}/edit/:id`,
  DASHBOARD_COMPANY_ADD: `${ROUTE_PATHS.DASHBOARD_COMPANIES}/add`,
  DASHBOARD_PROPERTY_EDIT: `${ROUTE_PATHS.DASHBOARD_PROPERTIES}/edit/:id`,
  DASHBOARD_PROPERTY_ADD: `${ROUTE_PATHS.DASHBOARD_PROPERTIES}/add`,
  DASHBOARD_INVITEE_EDIT: `${ROUTE_PATHS.DASHBOARD_INVITEE}/edit/:code`,
  DASHBOARD_INVITEE_ADD: `${ROUTE_PATHS.DASHBOARD_INVITEE}/add`,
  DASHBOARD_YARDI_INVITE_SEARCH: `${ROUTE_PATHS.DASHBOARD_YARDI_INVITE}/search`,
  DASHBOARD_YARDI_MATCHING_PROPERTY: `${ROUTE_PATHS.DASHBOARD_YARDI_MATCHING}/property/:propertyId`,
  DASHBOARD_YARDI_MATCHING_RUN: `${ROUTE_PATHS.DASHBOARD_YARDI_MATCHING}/runs/:runId`,
  DASHBOARD_USER_MANAGEMENT_EDIT: `${ROUTE_PATHS.DASHBOARD_USER_MANAGEMENT}/edit/:userId`,
  DASHBOARD_USER_MANAGEMENT_ROLES: `${ROUTE_PATHS.DASHBOARD_USER_MANAGEMENT}/roles/:userId`,
  DASHBOARD_USER_MANAGEMENT_ADD: `${ROUTE_PATHS.DASHBOARD_USER_MANAGEMENT}/add`,
  DASHBOARD_PRODUCT_EDIT: `${ROUTE_PATHS.DASHBOARD_PRODUCTS}/edit/:productId`,
  DASHBOARD_PRODUCT_ADD: `${ROUTE_PATHS.DASHBOARD_PRODUCTS}/add`,
  DASHBOARD_MODEL_EDIT: `${ROUTE_PATHS.DASHBOARD_MODELS}/edit/:modelId`,
  DASHBOARD_MODEL_ADD: `${ROUTE_PATHS.DASHBOARD_MODELS}/add`,
  DASHBOARD_EMAIL_TEMPLATES_EDIT: `${ROUTE_PATHS.DASHBOARD_EMAIL_TEMPLATES}/edit/:templateId`,
  DASHBOARD_EMAIL_TEMPLATES_ADD: `${ROUTE_PATHS.DASHBOARD_EMAIL_TEMPLATES}/add`,
  DASHBOARD_APPLICATION_DETAIL: `${ROUTE_PATHS.DASHBOARD_APPLICATIONS}/:applicantId`,
  DASHBOARD_RESIDENT_DETAIL: `${ROUTE_PATHS.DASHBOARD_RESIDENTS}/:pagetype/:applicantId/:userId`,
  DEFAULT_DETAIL: `${ROUTE_PATHS.DEFAULTS}/:pagetype/:applicantId/:userId`,
  /** @deprecated Use DEFAULT_DETAIL */
  DASHBOARD_REIMBURSEMENT_DETAIL: `${ROUTE_PATHS.DEFAULTS}/:pagetype/:applicantId/:userId`,
  DASHBOARD_TRANSFER_DETAIL_WITH_COMPANY: `${ROUTE_PATHS.DASHBOARD_TRANSFER}/:id/:companyId`,
  DASHBOARD_TRANSFER_DETAIL: `${ROUTE_PATHS.DASHBOARD_TRANSFER}/:id`,
  DASHBOARD_REPORT_PAYMENT_LOG_DETAIL: '/reports/payment-logs/:logId',
  DASHBOARD_REPORT_PAYMENT_LOG_ADJUSTMENT_DETAIL:
    '/reports/payment-logs/detail/:logId',
  DASHBOARD_REPORT_DEFAULT_PAYMENT_LOG_DETAIL:
    '/reports/default-payment-logs/:logId',
  DASHBOARD_REPORT_CHARGEBACK_DETAIL:
    '/reports/chargeback/detail/:applicantId/:logId',
  APPLICANT_APPLICATION_TAB: '/applicant/application/:tab',
  RESIDENT_APPLICATION_DETAIL: '/resident-application/:applicantId',
} as const

export const ROUTE_BUILDERS = {
  DASHBOARD_APPLICATION_DETAIL: (applicantId: string | number) =>
    `${ROUTE_PATHS.DASHBOARD_APPLICATIONS}/${applicantId}`,
  RESIDENT_DETAIL: (applicantId: string | number, userId: string | number) =>
    `${ROUTE_PATHS.DASHBOARD_RESIDENTS}/resident-detail/${applicantId}/${userId}`,
  DEFAULT_DETAIL: (applicantId: string | number, userId: string | number) =>
    `${ROUTE_PATHS.DEFAULTS}/default-detail/${applicantId}/${userId}`,
} as const

/**
 * Old React `/dashboard/...` paths → Angular-shaped paths (bookmarks / one release).
 * Longer prefixes first.
 */
export const LEGACY_DASHBOARD_REDIRECTS: ReadonlyArray<{
  from: string
  to: string
}> = [
  {
    from: '/dashboard/document-management/terms',
    to: '/terms-and-conditions',
  },
  {
    from: '/dashboard/document-management/service',
    to: '/gdpr-consent/gdpr-document',
  },
  {
    from: '/dashboard/document-management',
    to: '/terms-and-conditions',
  },
  {
    from: '/dashboard/business-config/screening-info-requests',
    to: '/screening-info-requests',
  },
  {
    from: '/dashboard/business-config/reimbursement-notes',
    to: '/frequently-used-notes',
  },
  {
    from: '/dashboard/property-maintenance/transfer',
    to: '/transfer-list',
  },
  {
    from: '/dashboard/property-maintenance/import',
    to: '/import-data',
  },
  {
    from: '/dashboard/property-maintenance/teams',
    to: '/sales-team',
  },
  {
    from: '/dashboard/property-maintenance',
    to: '/property-maintenance',
  },
  { from: '/dashboard/config/underwriting', to: '/underwriting-config' },
  { from: '/dashboard/reports-scheduler', to: '/reports-scheduler' },
  { from: '/dashboard/stock-registry', to: '/stock' },
  { from: '/dashboard/whats-new', to: '/version' },
  { from: '/dashboard/roles', to: '/user-roles' },
  { from: '/dashboard/yardi-invite', to: '/yardiinvite' },
  { from: '/dashboard/companies', to: '/company' },
  { from: '/dashboard/properties', to: '/property' },
  { from: '/dashboard/products', to: '/product' },
  { from: '/dashboard/models', to: '/model' },
  { from: '/dashboard/invitee', to: '/invite' },
  { from: '/dashboard/invite', to: '/invite' },
  { from: '/dashboard/applications', to: '/application' },
  {
    from: '/dashboard/residents/resident-detail',
    to: '/resident/resident-detail',
  },
  { from: '/dashboard/residents', to: '/resident' },
  {
    from: '/dashboard/reimbursements/detail',
    to: '/defaults/default-detail',
  },
  { from: '/dashboard/reimbursements', to: '/defaults' },
  {
    from: '/reimbursements/detail',
    to: '/defaults/default-detail',
  },
  { from: '/reimbursements', to: '/defaults' },
  { from: '/dashboard/reports', to: '/reports' },
  { from: '/dashboard/email-templates', to: '/email-templates' },
  { from: '/dashboard/user-management', to: '/user-management' },
  { from: '/dashboard/transfer', to: '/transfer' },
  { from: '/dashboard/help-about', to: '/help-about' },
  { from: '/dashboard/profile', to: '/user/profile' },
  { from: '/dashboard/impact-notes', to: '/impact-notes' },
  { from: '/dashboard/report-description', to: '/report-description' },
  { from: '/dashboard/jobs', to: '/jobs' },
  { from: '/dashboard/notification-settings', to: '/notification-settings' },
  { from: '/dashboard/policy', to: '/policy' },
  { from: '/dashboard/organizations', to: '/organizations' },
  { from: '/dashboard/system-announcement', to: '/system-announcement' },
  { from: '/dashboard/dashboard-tiles', to: '/dashboard-tiles' },
  { from: '/dashboard/mailtrap', to: '/mailtrap' },
  { from: '/dashboard/config', to: '/config' },
] as const

/** Rewrite a legacy `/dashboard/...` or `/reimbursements` pathname to Angular `/defaults`. */
export function mapLegacyDashboardPath(pathname: string): string | null {
  for (const { from, to } of LEGACY_DASHBOARD_REDIRECTS) {
    if (pathname === from || pathname.startsWith(`${from}/`)) {
      return `${to}${pathname.slice(from.length)}`
    }
  }
  return null
}

/**
 * Main application routes configuration
 * This centralizes route definitions for use in routing and navigation
 * Currently empty - routes will be added as features are implemented
 */
export const routes: RouteConfig[] = []

/**
 * Get routes that should appear in navigation
 */
export function getNavigationRoutes(): RouteConfig[] {
  return routes.filter((route) => route.requiresAuth && route.icon)
}
